import { createTheme } from '@mui/material/styles';
import { colors, gradients, shadows } from './colors';

// Healthcare Teal/Cyan Palette - aligned with src/styles/variables.scss
const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
      light: colors.primaryLight,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary,
      dark: colors.secondaryDark,
      light: colors.secondaryLight,
      contrastText: '#ffffff',
    },
    error: { main: colors.error, dark: colors.errorDark, light: colors.errorLight },
    warning: { main: colors.warning, dark: colors.warningDark, light: colors.warningLight },
    success: { main: colors.success, dark: colors.successDark, light: colors.successLight },
    info: { main: colors.info, dark: colors.infoDark, light: colors.infoLight },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    background: {
      default: colors.background,
      paper: colors.backgroundPaper,
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.3 },
    h2: { fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.35 },
    h3: { fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.4 },
    h4: { fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '0.875rem', lineHeight: 1.65 },
    body2: { fontSize: '0.75rem', lineHeight: 1.6 },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.005em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  // Cyan-tinted layered shadows — replaces all rgba(0,0,0,...) entries
  // MUST stay at exactly 25 entries (MUI requirement)
  shadows: [
    'none',
    shadows.sm,
    shadows.md,
    shadows.md,
    shadows.lg,
    shadows.lg,
    shadows.xl,
    shadows.xl,
    '0 16px 40px -16px rgba(8, 145, 178, 0.25), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 20px 48px -18px rgba(8, 145, 178, 0.28), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 24px 56px -20px rgba(8, 145, 178, 0.30), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 28px 64px -22px rgba(8, 145, 178, 0.32), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 32px 72px -24px rgba(8, 145, 178, 0.34), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 36px 80px -26px rgba(8, 145, 178, 0.36), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 40px 88px -28px rgba(8, 145, 178, 0.38), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 44px 96px -30px rgba(8, 145, 178, 0.40), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 48px 104px -32px rgba(8, 145, 178, 0.42), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 52px 112px -34px rgba(8, 145, 178, 0.44), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 56px 120px -36px rgba(8, 145, 178, 0.46), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 60px 128px -38px rgba(8, 145, 178, 0.48), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 64px 136px -40px rgba(8, 145, 178, 0.50), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 68px 144px -42px rgba(8, 145, 178, 0.52), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 72px 152px -44px rgba(8, 145, 178, 0.54), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 76px 160px -46px rgba(8, 145, 178, 0.56), 0 2px 6px rgba(15, 23, 42, 0.06)',
    '0 80px 168px -48px rgba(8, 145, 178, 0.58), 0 2px 6px rgba(15, 23, 42, 0.06)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px', // was '1rem' (16px)
          padding: '10px 20px',
          minHeight: '40px',
          boxShadow: 'none',
          fontWeight: 600,
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(20, 184, 166, 0.30)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(1px)',
          },
        },
        containedPrimary: {
          background: gradients.primary,
          '&:hover': {
            background: gradients.primaryHover,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px', // was '16px'
          boxShadow: shadows.md, // cyan-tinted
          border: `1px solid ${colors.borderSoft}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: shadows.lg,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // disable MUI default elevation tinting
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px', // was '12px' — align with buttons
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary,
              boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.10)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 600,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.textPrimary,
          fontSize: '0.75rem',
          borderRadius: '6px',
          padding: '6px 10px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          boxShadow: shadows.xl,
        },
      },
    },
  },
});

export default theme;
